import React, { useState, useMemo, useEffect } from 'react';
import GuidedRemovalScreen from './GuidedRemovalScreen';
import GuidedPamperingScreen from './GuidedPamperingScreen';
import GuidedDesignArtScreen from './GuidedDesignArtScreen';
import GuidedNailsCombinedScreen from './GuidedNailsCombinedScreen';
import GuidedWaxTypeScreen from './GuidedWaxTypeScreen';
import './GuidedAddOns.css';

function GuidedAddOnsFlow({ selectedServices, serviceAddOns, nailServiceIds, waxServiceIds, selectedOptionalAddOns, onToggleAddOn, onComplete, onBack, getCategoryForService }) {
  const [currentServiceIdx, setCurrentServiceIdx] = useState(0);
  const [currentScreenIdx, setCurrentScreenIdx] = useState(0);

  // Build per-service screen lists
  const serviceFlows = useMemo(() => {
    const flows = [];

    for (const svc of selectedServices) {
      const optionalAddOns = serviceAddOns[svc.id]?.optional || [];
      const isNail = nailServiceIds?.has(svc.id);
      const isWax = waxServiceIds?.has(svc.id);
      const screens = [];

      if (isWax) {
        // Wax services: group optional add-ons by sub_category_name
        if (optionalAddOns.length === 0) continue;
        const groups = {};
        for (const addOn of optionalAddOns) {
          const subCat = (addOn.sub_category_name || '').toLowerCase().trim();
          if (!groups[subCat]) groups[subCat] = [];
          groups[subCat].push(addOn);
        }

        const hardWax = groups['hard wax'] || [];
        // All non-hard-wax add-ons go to a pampering-style screen
        const otherAddOns = optionalAddOns.filter(a =>
          !(a.sub_category_name || '').toLowerCase().trim().includes('hard wax')
        );

        // Wax type screen: strip wax (default/included) vs hard wax upgrade + other add-ons
        if (hardWax.length > 0 || otherAddOns.length > 0) {
          screens.push({
            type: 'wax_type',
            hardWaxAddOns: hardWax,
            otherAddOns
          });
        }
      } else if (isNail) {
        // Group by sub_category_name for nail services
        if (optionalAddOns.length === 0) continue;
        const groups = {};
        for (const addOn of optionalAddOns) {
          const subCat = (addOn.sub_category_name || '').toLowerCase().trim();
          if (!groups[subCat]) groups[subCat] = [];
          groups[subCat].push(addOn);
        }

        const removals = groups['removals'] || groups['removal'] || [];
        if (removals.length > 0) {
          screens.push({ type: 'removal', addOns: removals });
        }

        const pampering = groups['pampering'] || [];
        const design = groups['design'] || [];
        const nailArt = groups['nail art'] || [];

        // Any uncategorized add-ons go into pampering
        const categorized = new Set([
          ...(groups['removals'] || []), ...(groups['removal'] || []),
          ...pampering, ...design, ...nailArt
        ]);
        const uncategorized = optionalAddOns.filter(a => !categorized.has(a));
        const allPampering = [...pampering, ...uncategorized];

        // Combined screen: finishes + nail art + upgrades on one page
        if (allPampering.length > 0 || design.length > 0 || nailArt.length > 0) {
          screens.push({
            type: 'nails_combined',
            pamperingAddOns: allPampering,
            designAddOns: design,
            nailArtAddOns: nailArt
          });
        }
      } else {
        // Non-nail, non-wax: single screen with all optional add-ons
        if (optionalAddOns.length === 0) continue;
        screens.push({ type: 'pampering', addOns: optionalAddOns });
      }

      if (screens.length > 0) {
        // Simplify nail service names: "Structured Gel Manicure (BIAB)" → "Manicure"
        // Keep waxing service names as-is: "Brazilian" stays "Brazilian"
        let simpleName;
        if (isNail && getCategoryForService) {
          const cat = getCategoryForService(svc.id).toLowerCase();
          simpleName = cat.includes('nails feet') ? 'Pedicure'
            : cat.includes('nails hands') ? 'Manicure'
            : 'Nails';
        } else if (isNail) {
          const fullName = (svc.display_name || svc.name || '').toLowerCase();
          simpleName = fullName.includes('pedicure') ? 'Pedicure'
            : fullName.includes('manicure') || fullName.includes('mani') ? 'Manicure'
            : 'Nails';
        } else {
          simpleName = svc.display_name || svc.name;
        }
        flows.push({
          serviceId: svc.id,
          serviceName: simpleName,
          isNail: !!isNail,
          isWax: !!isWax,
          screens
        });
      }
    }

    return flows;
  }, [selectedServices, serviceAddOns, nailServiceIds, waxServiceIds]);

  const currentFlow = serviceFlows[currentServiceIdx];
  const currentScreen = currentFlow?.screens[currentScreenIdx];

  const shouldComplete = !currentFlow || !currentScreen;
  useEffect(() => {
    if (shouldComplete) onComplete();
  }, [shouldComplete, onComplete]);

  if (shouldComplete) return null;

  function handleNext() {
    if (currentScreenIdx < currentFlow.screens.length - 1) {
      setCurrentScreenIdx(currentScreenIdx + 1);
    } else if (currentServiceIdx < serviceFlows.length - 1) {
      setCurrentServiceIdx(currentServiceIdx + 1);
      setCurrentScreenIdx(0);
    } else {
      onComplete();
    }
  }

  function handleBack() {
    if (currentScreenIdx > 0) {
      setCurrentScreenIdx(currentScreenIdx - 1);
    } else if (currentServiceIdx > 0) {
      const prevFlow = serviceFlows[currentServiceIdx - 1];
      setCurrentServiceIdx(currentServiceIdx - 1);
      setCurrentScreenIdx(prevFlow.screens.length - 1);
    } else {
      onBack();
    }
  }

  const selectedCount = selectedOptionalAddOns.filter(a => a.parentServiceId === currentFlow.serviceId).length;

  return (
    <div className="step-content guided-flow-container">
      <h1 className="step-title" style={{ fontSize: 22 }}>
        {currentScreen.type === 'removal'
          ? 'DO YOU NEED REMOVAL?'
          : currentScreen.type === 'wax_type'
            ? (currentFlow.serviceName || 'CHOOSE YOUR WAX').toUpperCase()
            : currentScreen.type === 'designart'
              ? 'DESIGNS'
              : currentScreen.type === 'nails_combined'
                ? 'ADD-ONS & DESIGNS'
                : 'ADD-ONS'
        }
      </h1>
      {(serviceFlows.length > 1 || (!currentFlow.isNail && currentScreen.type !== 'wax_type')) && (
        <p className="step-subtitle">{currentFlow.serviceName}</p>
      )}

      {/* Current screen */}
      {currentScreen.type === 'removal' && (
        <GuidedRemovalScreen
          addOns={currentScreen.addOns}
          selectedAddOns={selectedOptionalAddOns}
          onToggleAddOn={onToggleAddOn}
          parentServiceId={currentFlow.serviceId}
          serviceName={currentFlow.serviceName}
          onSkip={handleNext}
        />
      )}
      {currentScreen.type === 'pampering' && (
        <GuidedPamperingScreen
          addOns={currentScreen.addOns}
          selectedAddOns={selectedOptionalAddOns}
          onToggleAddOn={onToggleAddOn}
          parentServiceId={currentFlow.serviceId}
          serviceName={currentFlow.serviceName}
          onSkip={handleNext}
        />
      )}
      {currentScreen.type === 'designart' && (
        <GuidedDesignArtScreen
          designAddOns={currentScreen.designAddOns}
          nailArtAddOns={currentScreen.nailArtAddOns}
          selectedAddOns={selectedOptionalAddOns}
          onToggleAddOn={onToggleAddOn}
          parentServiceId={currentFlow.serviceId}
          serviceName={currentFlow.serviceName}
          onSkip={handleNext}
        />
      )}
      {currentScreen.type === 'nails_combined' && (
        <GuidedNailsCombinedScreen
          pamperingAddOns={currentScreen.pamperingAddOns}
          designAddOns={currentScreen.designAddOns}
          nailArtAddOns={currentScreen.nailArtAddOns}
          selectedAddOns={selectedOptionalAddOns}
          onToggleAddOn={onToggleAddOn}
          parentServiceId={currentFlow.serviceId}
          serviceName={currentFlow.serviceName}
          onSkip={handleNext}
        />
      )}
      {currentScreen.type === 'wax_type' && (
        <GuidedWaxTypeScreen
          hardWaxAddOns={currentScreen.hardWaxAddOns}
          otherAddOns={currentScreen.otherAddOns}
          selectedAddOns={selectedOptionalAddOns}
          onToggleAddOn={onToggleAddOn}
          parentServiceId={currentFlow.serviceId}
          serviceName={currentFlow.serviceName}
          onSkip={handleNext}
        />
      )}

      {/* Bottom bar */}
      <div className="guided-bottom-bar">
        <button className="guided-btn-back" onClick={handleBack}>BACK</button>
        <button className="guided-btn-next" onClick={handleNext}>
          {selectedCount > 0 ? `NEXT — ${selectedCount} ADD-ON${selectedCount > 1 ? 'S' : ''}` : 'NEXT'}
        </button>
      </div>
    </div>
  );
}

export default GuidedAddOnsFlow;
